import { api } from '$lib/utils/api.js';
import { createMutation, createQuery } from '@tanstack/svelte-query';

const ONE_DAY_MS = 1000 * 60 * 60 * 24;

export const getHealthPlanActions = (clientId) =>
  createQuery({
    queryKey: ['healthplan-actions', clientId],
    queryFn: () => api().get(`nurse/clients/${clientId}/healthPlanAction`),
  });

export const createHealthPlanAction = ({ clientId, title, description }) =>
  api().post(`v2/clients/${clientId}/healthPlans`, { title, description });

export const updateClientHealthPlan = ({ clientId, healthPlanActions }) =>
  api().post(`v2/clients/${clientId}/healthPlans?reset`, healthPlanActions);

export const getClientOverview = (clientId) =>
  createQuery({
    queryKey: ['client-overview', clientId],
    queryFn: () => api().get(`nurse/clients/${clientId}/overview`),
  });

export const clientInfoOptions = (clientId) => ({
  queryKey: ['client-info', clientId],
  queryFn: () => api().get(`nurse/clients/${clientId}/info`),
});

export const getClientInfo = (clientId) =>
  createQuery(clientInfoOptions(clientId));

export const useGetClientOverviewQuery = ({ clientId }, options = {}) =>
  createQuery({
    queryKey: ['client-overview', clientId],
    queryFn: () => api().get(`nurse/clients/${clientId}/overview`),
    ...options,
  });

/**
 * Options only. createQuery reads the query-client context via getContext,
 * which throws once the component is past init — so a caller whose inputs
 * change while mounted must build a store of these instead of re-creating the
 * query from a $derived.
 */
export const clientQueryOptions = ({ clientId }, options = {}) => ({
  queryKey: ['clientProfile', clientId],
  queryFn: () => api().get(`nurse/clients/${clientId}`),
  ...options,
});

export const useGetClientQuery = (props, options = {}) =>
  createQuery(clientQueryOptions(props, options));

export const useRegistrationQuery = ({ clientId }, options = {}) =>
  createQuery({
    queryKey: ['registration', clientId],
    queryFn: () => api().get(`nurse/clients/${clientId}/registration`),
    ...options,
  });

export const useDeleteRegistrationMutation = (options = {}) =>
  createMutation({
    mutationKey: ['deleteRegistration'],
    mutationFn: ({ clientId }) =>
      api().delete(`nurse/clients/${clientId}/registration`),
    ...options,
  });

export const impersonateClient = (clientId) =>
  api().get(`nurse/clients/${clientId}/impersonate`);
export const becomeClient = (clientId) =>
  api().get(`nurse/clients/${clientId}/become`);

export const useClientReports = ({ clientId }, options = {}) =>
  createQuery({
    queryKey: ['clientReports', clientId],
    queryFn: () => api().get(`nurse/clients/${clientId}/reports`),
    ...options,
  });

export const useClientBookingsQuery = (clientId) =>
  createQuery({
    queryKey: ['bookings', clientId],
    queryFn: () => api().get(`nurse/clients/${clientId}/bookings`),
  });

/**
 * Query key + fetcher on their own, so callers that need a *reactive* query can
 * build an options store from them. createQuery only runs during component
 * init, but it accepts a store of options, which is how a query stays reactive
 * without being re-created.
 */
export const notesQueryOptions = (clientId) => ({
  queryKey: ['notes', clientId],
  queryFn: () => api().get(`v2/consultationHistory/${clientId}/consultations`),
});

export const useNotesQuery = ({ clientId }, options = {}) =>
  createQuery({ ...notesQueryOptions(clientId), ...options });

export const useSaveNoteMutation = (options = {}) =>
  createMutation({
    mutationKey: ['saveNote'],
    mutationFn: ({
      clientId,
      reportId,
      notes,
      consultationAreaIds,
      contactType,
    }) =>
      api().post('v2/consultationHistory', {
        clientId,
        reportId,
        notes,
        consultationAreaIds,
        contactType,
      }),
    ...options,
  });

export const useDeleteNoteMutation = (options = {}) =>
  createMutation({
    mutationKey: ['deleteNote'],
    mutationFn: ({ noteId, reason }) =>
      api().delete(`nurse/consultationHistory/${noteId}`, { reason }),
    ...options,
  });

export const useSetConsultationHistoryToSignedMutation = (options = {}) =>
  createMutation({
    mutationKey: ['setConsultationHistoryToSigned'],
    mutationFn: ({ noteId, user }) =>
      api().put(`v2/consultationHistory/${noteId}/signedBy`, { user }),
    ...options,
  });

export const useEditConsultationHistoryNoteMutation = (options = {}) =>
  createMutation({
    mutationKey: ['editConsultationHistoryNote'],
    mutationFn: ({ noteId, note, consultationAreas, contactType }) =>
      api().put(`v2/consultationHistory/${noteId}/note`, {
        notes: note,
        consultationAreas,
        contactType,
      }),
    ...options,
  });

export const useDeleteConsultationHistoryMutation = (options = {}) =>
  createMutation({
    mutationKey: ['deleteConsultationHistory'],
    mutationFn: ({ noteId }) =>
      api().delete(`v2/consultationHistory/${noteId}/delete`),
    ...options,
  });

export const getTopics = () =>
  createQuery({
    queryKey: ['topics'],
    queryFn: () => api().get('nurse/calendar/topics'),
  });

export const getConsultationAreas = () =>
  createQuery({
    queryKey: ['consultationAreas'],
    queryFn: () => api().get('nurse/consultationAreas'),
    gcTime: ONE_DAY_MS,
    staleTime: ONE_DAY_MS,
  });

export const useRolesQuery = (props, options = {}) =>
  createQuery({
    // `props` belongs in the key: it is passed to the request, and without it
    // this shares a cache entry with getRoles() below, which fetches the
    // unfiltered list under the same key.
    queryKey: ['roles', props],
    queryFn: () => api().get('nurse/users/roles', props),
    ...options,
  });

export const getRoles = () =>
  createQuery({
    queryKey: ['roles'],
    queryFn: () => api().get('nurse/users/roles'),
    gcTime: ONE_DAY_MS,
    staleTime: ONE_DAY_MS,
  });

export const useAddClientMutation = (options = {}) =>
  createMutation({
    mutationKey: ['addClient'],
    mutationFn: ({ clientData }) =>
      api().post('nurse/clients/', { ...clientData }),
    ...options,
  });

export const useDeleteClientMutation = (options = {}) =>
  createMutation({
    mutationKey: ['deleteClient'],
    mutationFn: ({ clientIds }) => api().delete('nurse/clients/', clientIds),
    ...options,
  });

export const updateClient = (clientData) =>
  api().put(`nurse/clients/${clientData.id}`, { ...clientData });

/**
 * `props` is part of the key: the list is filtered server-side, so a predicate
 * change has to produce a new cache entry. It was previously keyed on
 * ['allClients'] alone, which meant filter changes never invalidated.
 */
export const allClientsOptions = (props) => ({
  queryKey: ['allClients', props],
  queryFn: () => api().get('v2/clientsList', props),
});

export const useGetAllClientsQuery = (props, options = {}) =>
  createQuery({ ...allClientsOptions(props), ...options });

export const useMergeClientsMutation = (options = {}) =>
  createMutation({
    mutationKey: ['mergeClients'],
    mutationFn: (props) => api().post('v2/clients/merge', props),
    ...options,
  });

export const useGetClientByIdQuery = (clientId) =>
  createQuery({
    queryKey: ['client-by-id-v2', clientId],
    queryFn: () => api().get(`v2/clients/${clientId}`),
  });

export const useDeleteClientRegistrationMutation = (options = {}) =>
  createMutation({
    mutationKey: ['deleteClientRegistration'],
    mutationFn: ({ clientId, sendRegistrationEmail }) =>
      api().delete(`v2/clients/${clientId}/registration`, {
        sendRegistrationEmail,
      }),
    ...options,
  });

export const useMergeClientsPreviewMutation = (options = {}) =>
  createMutation({
    mutationKey: ['mergeClientsPreview'],
    mutationFn: (props) => api().post('v2/clients/merge/preview', props),
    ...options,
  });
